import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryData } from '@/mocks/categories';

type Category = 'dress' | 'jewelry' | 'hanbok';

const categoryFields: Record<Category, { label: string; fields: { label: string; type: string; options?: string[] }[] }> = {
  dress: {
    label: '예복',
    fields: [
      { label: '예산 범위', type: 'select', options: ['150~250만원', '250~350만원', '350~500만원', '500만원 이상'] },
      { label: '제작 방식', type: 'select', options: ['대여', '맞춤', '반수제', '수제'] },
      { label: '본식일', type: 'date' },
      { label: '촬영 대여복 필요 여부', type: 'select', options: ['예', '아니오', '미정'] },
      { label: '방문 지역', type: 'select', options: ['강남', '홍대/합정', '청담/압구정', '종로', '기타'] },
    ],
  },
  jewelry: {
    label: '예물',
    fields: [
      { label: '예산 범위', type: 'select', options: ['100~250만원', '250~400만원', '400~600만원', '600만원 이상'] },
      { label: '커플링/다이아 여부', type: 'select', options: ['커플링만', '다이아 포함', '둘 다'] },
      { label: '브랜드/공방 선호', type: 'select', options: ['브랜드', '공방', '둘 다'] },
      { label: '방문 지역', type: 'select', options: ['강남', '성수/서울숲', '명동', '기타'] },
    ],
  },
  hanbok: {
    label: '한복',
    fields: [
      { label: '착용 대상', type: 'select', options: ['신랑신부만', '혼주 포함', '미정'] },
      { label: '맞춤/대여', type: 'select', options: ['맞춤', '대여', '둘 다 고민 중'] },
      { label: '예산 범위', type: 'select', options: ['50~100만원', '100~200만원', '200~400만원', '400만원 이상'] },
      { label: '피팅 지역', type: 'select', options: ['종로/인사동', '강남', '압구정', '기타'] },
    ],
  },
};

export default function Quote() {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>();
  const [submitted, setSubmitted] = useState(false);

  const toggleCategory = useCallback((cat: Category) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  }, []);

  const setField = useCallback((cat: Category, fieldLabel: string, value: string) => {
    setFormData((prev) => ({ ...prev, [`${cat}-${fieldLabel}`]: value }));
  }, []);

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-[100dvh] bg-cream flex flex-col items-center justify-center px-8">
        <div className="w-16 h-16 rounded-full bg-navy flex items-center justify-center mb-6">
          <i className="ri-check-line text-white text-2xl" />
        </div>
        <h2 className="text-navy text-xl font-bold text-center mb-3">
          요청이 접수되었어요
        </h2>
        <p className="text-beige-dark text-sm text-center leading-relaxed mb-8">
          조건에 맞는 업체가 순차적으로
          <br />
          제안을 보내드립니다
        </p>
        <div className="w-full space-y-3">
          <button
            onClick={() => navigate('/quotes')}
            className="w-full bg-navy text-white rounded-xl py-4 font-semibold text-base cursor-pointer hover:bg-navy-light transition-colors whitespace-nowrap"
          >
            견적함으로 이동
          </button>
          <button
            onClick={() => navigate('/home')}
            className="w-full bg-white text-navy rounded-xl py-4 font-medium text-base border border-cream-dark cursor-pointer hover:bg-cream transition-colors whitespace-nowrap"
          >
            홈으로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <div className="px-5 pt-6 pb-4 bg-white border-b border-cream-dark">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate('/home')}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <h1 className="text-navy text-xl font-bold">견적 요청</h1>
        </div>
        <p className="text-beige-dark text-sm">견적을 요청할 항목을 선택하고 조건을 입력해주세요</p>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-6">
        {/* Category Selection */}
        <div className="mb-6">
          <p className="text-navy font-semibold text-sm mb-3">어떤 항목의 견적이 필요한가요?</p>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(categoryData) as Category[]).map((cat) => {
              const info = categoryData[cat];
              const selected = selectedCategories.includes(cat);
              return (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  className={`p-3 rounded-xl border-2 text-center transition-all cursor-pointer ${
                    selected
                      ? 'border-navy bg-navy/5'
                      : 'border-cream-dark bg-white hover:border-beige'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-1.5 ${
                      selected ? 'bg-navy text-white' : 'bg-cream text-navy'
                    }`}
                  >
                    <i className={`${info.icon} text-lg`} />
                  </div>
                  <p className={`text-xs font-medium ${selected ? 'text-navy' : 'text-gray-600'}`}>
                    {info.name}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Category Forms */}
        {selectedCategories.map((cat) => {
          const info = categoryData[cat];
          const fields = categoryFields[cat];
          return (
            <div key={cat} className="mb-6 bg-white rounded-2xl p-5 border border-cream-dark">
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${info.color}12`, color: info.color }}
                >
                  <i className={`${info.icon} text-sm`} />
                </div>
                <p className="font-semibold text-navy">{fields.label} 견적 조건</p>
              </div>
              <div className="space-y-4">
                {fields.fields.map((field) => (
                  <div key={field.label}>
                    <label className="text-beige-dark text-xs mb-1.5 block">{field.label}</label>
                    {field.type === 'select' && field.options ? (
                      <div className="flex flex-wrap gap-2">
                        {field.options.map((opt) => {
                          const selected = formData[`${cat}-${field.label}`] === opt;
                          return (
                            <button
                              key={opt}
                              onClick={() => setField(cat, field.label, opt)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                                selected
                                  ? 'border-navy bg-navy text-white'
                                  : 'border-cream-dark bg-white text-gray-600 hover:border-beige'
                              }`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <input
                        type={field.type}
                        onChange={(e) => setField(cat, field.label, e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border-2 border-cream-dark bg-white text-navy text-sm focus:border-navy focus:outline-none"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-3 bg-white border-t border-cream-dark">
        <button
          onClick={handleSubmit}
          disabled={selectedCategories.length === 0}
          className={`w-full py-4 rounded-xl font-semibold text-base transition-all cursor-pointer whitespace-nowrap ${
            selectedCategories.length > 0
              ? 'bg-navy text-white hover:bg-navy-light'
              : 'bg-cream-dark text-beige-dark cursor-not-allowed'
          }`}
        >
          견적 요청하기
        </button>
      </div>
    </div>
  );
}